/**
 * PlacementModeIndicator Component
 * Bottom bar showing current placement mode with count and cancel option
 */

import React from 'react';
import { X, Lightbulb, SquareEqual, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { sanitizeSVG } from '@/lib/sanitize';
import { unescapeSvg, resizeSvg } from '@/lib/svgNormalizer';
import type { PlacementItem } from '../hooks/useUnifiedPlacement';

interface PlacementModeIndicatorProps {
  currentItem: PlacementItem | null;
  placementCount: number;
  onClear: () => void;
  className?: string;
}

export function PlacementModeIndicator({
  currentItem,
  placementCount,
  onClear,
  className,
}: PlacementModeIndicatorProps) {
  if (!currentItem) return null;

  // Get icon based on type
  const renderIcon = () => {
    if (currentItem.iconSvg) {
      // Clean and resize the SVG - normalization happens at upload time
      const cleanedSvg = unescapeSvg(currentItem.iconSvg);
      const processedSvg = resizeSvg(cleanedSvg, 20, 20);
      const targetColor = currentItem.iconColor || '#6B7280';

      return (
        <div
          className="flex items-center justify-center"
          style={{ color: targetColor }} // CSS currentColor inheritance for dynamic colors
          dangerouslySetInnerHTML={{ __html: sanitizeSVG(processedSvg) }}
        />
      );
    }

    if (currentItem.iconUrl) {
      return <img src={currentItem.iconUrl} alt="" className="w-5 h-5 object-contain" />;
    }

    // Fallback icons based on type
    switch (currentItem.type) {
      case 'fixture':
        return (
          <Lightbulb className="w-5 h-5" style={{ color: currentItem.iconColor || '#3b82f6' }} />
        );
      case 'control':
        return (
          <SquareEqual className="w-5 h-5" style={{ color: currentItem.iconColor || '#8b5cf6' }} />
        );
      case 'product':
        return (
          <Package className="w-5 h-5" style={{ color: currentItem.iconColor || '#10b981' }} />
        );
      default:
        return <Package className="w-5 h-5" />;
    }
  };

  return (
    <div
      className={cn(
        'fixed bottom-4 left-1/2 -translate-x-1/2 z-[1000]',
        'bg-background border rounded-lg shadow-lg',
        'flex items-center gap-3 px-4 py-2',
        'animate-in slide-in-from-bottom-4 duration-200',
        className,
      )}
    >
      {/* Icon */}
      <div className="flex items-center justify-center w-8 h-8 rounded-md bg-accent">
        {renderIcon()}
      </div>

      {/* Item name */}
      <div className="flex flex-col min-w-0">
        <span className="text-sm font-medium truncate max-w-[200px]">
          {currentItem.displayName}
        </span>
        <span className="text-xs text-muted-foreground capitalize">{currentItem.type}</span>
      </div>

      {/* Divider */}
      <div className="h-8 w-px bg-border" />

      {/* Placement count */}
      <div className="flex items-center gap-1 text-sm">
        <span className="font-medium">{placementCount}</span>
        <span className="text-muted-foreground">placed</span>
      </div>

      {/* Divider */}
      <div className="h-8 w-px bg-border" />

      {/* Cancel button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={onClear}
        className="h-8 w-8 p-0 hover:bg-destructive/10 hover:text-destructive"
      >
        <X className="h-4 w-4" />
      </Button>

      {/* Hint text */}
      <span className="text-xs text-muted-foreground hidden sm:inline">
        Press <kbd className="px-1 py-0.5 bg-muted rounded text-[10px]">Esc</kbd> to cancel
      </span>
    </div>
  );
}

export default PlacementModeIndicator;
